import * as React from "react";
import { useState } from "react";
import { Box, Button, Chip, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { Close, InsertDriveFileOutlined, UploadFileOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";
import GenericDialog from "../../../../../components/dialog/generic-dialog.component";
import { ErrorComponent } from "../../../../../components/error/error.component";
import { translate } from "../../../../../translate/translate";
import { BaseService } from "../../../../../services/base-service";
import { URLS } from "../../../../../services/app-urls";
import { NpiChecklistItem } from "../../../../../models/npi/npi-checklist";
import { CHECKLIST_ITEM_STATUS_STYLES } from "../constants/dashboard.constants";
import { splitItemDescription } from "../utils/checklist.utils";

const TEXT_MAX_LENGTH = 5000;
const FILE_NAME_MAX_LENGTH = 256;
const FILES_MAX_SIZE_IN_BYTES = 10 * 1024 * 1024;
const ALLOWED_FILE_EXTENSIONS = /\.(jpe?g|png|pdf|xlsx?)$/i;
const FILES_INPUT_ID = "checklist-item-update-files";

const sectionTitleSx = { fontSize: "16px", fontWeight: 700, color: "#1B1F27" };

interface NpiChecklistItemUpdateDialogProps {
  item: NpiChecklistItem | null;
  onClose: () => void;
  onSaved: () => void;
}

const NpiChecklistItemUpdateDialogComponent = ({ item, onClose, onSaved }: NpiChecklistItemUpdateDialogProps) => {
  const [closedOn, setClosedOn] = useState<string>("");
  const [text, setText] = useState<string>("");
  const [files, setFiles] = useState<File[]>([]);

  if (!item) {
    return null;
  }

  const { title, detail } = splitItemDescription(item.description);
  const statusStyle = item.status ? CHECKLIST_ITEM_STATUS_STYLES[item.status] : null;
  const isBeforePhaseStart = !!closedOn && !!item.phase_start_date && closedOn < item.phase_start_date;
  const hasContent = !!closedOn || !!text.trim() || files.length > 0;

  const handleClose = () => {
    setClosedOn("");
    setText("");
    setFiles([]);
    onClose();
  };

  const handleFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files ?? []);
    event.target.value = "";

    const hasInvalidExtension = selectedFiles.some((file) => !ALLOWED_FILE_EXTENSIONS.test(file.name));
    if (hasInvalidExtension) {
      toast.error(translate.t("invalid_file"));
      return;
    }

    const hasLongName = selectedFiles.some((file) => file.name.length > FILE_NAME_MAX_LENGTH);
    if (hasLongName) {
      toast.error(translate.t("file_name_too_long"));
      return;
    }

    const nextFiles = [...files, ...selectedFiles];
    const totalSize = nextFiles.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > FILES_MAX_SIZE_IN_BYTES) {
      toast.error(translate.t("files_exceed_max_size"));
      return;
    }

    setFiles(nextFiles);
  };

  const handleSave = () => {
    const itemUpdateService = new BaseService<any>(URLS.NPI_CHECKLIST_ITEM_UPDATE);
    const indexedFiles = Object.fromEntries(files.map((file, index) => [`files[${index}]`, file]));
    itemUpdateService.save({ item: item.id, closed_on: closedOn || null, text: text.trim(), ...indexedFiles }, true).then(() => {
      toast.success(translate.t("successfully_registered"));
      onSaved();
      handleClose();
    }).catch((error: any) => {
      ErrorComponent(error);
    });
  };

  return (
    <GenericDialog
      open
      maxWidth="md"
      onClose={handleClose}
      title={"checklist_register_update"}
      colorButton={"primary"}
      labelButton={"save_form"}
      closeButtonText={"close"}
      disableConfirm={!hasContent || isBeforePhaseStart}
      handleConfirm={handleSave}>
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, p: 2, mb: 3, borderRadius: "8px", backgroundColor: "#DBEAFE" }}>
        <Box
          component="span"
          sx={{ flex: "0 0 auto", width: "auto", px: 1.25, py: 0.5, borderRadius: "6px", backgroundColor: "#2563EB", color: "#FFFFFF", fontSize: "13px", fontWeight: 600 }}>
          {item.item}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#1B1F27" }}>{title}</Typography>
          {detail && <Typography sx={{ fontSize: "13px", color: "#64748B" }}>{detail}</Typography>}
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 3 }}>
        <TextField disabled label={translate.t("days")} value={item.days ?? "-"} sx={{ width: 120 }} />
        <TextField
          disabled
          type="date"
          label={translate.t("checklist_final_deadline")}
          value={item.due_date ?? ""}
          slotProps={{ inputLabel: { shrink: true } }}
          sx={{ flex: 1 }}
        />
        <TextField
          type="date"
          label={translate.t("checklist_completion_date")}
          value={closedOn}
          onChange={(event) => setClosedOn(event.target.value)}
          error={isBeforePhaseStart}
          helperText={isBeforePhaseStart ? translate.t("checklist_closed_on_before_phase_start") : undefined}
          slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: item.phase_start_date ?? undefined } }}
          sx={{ flex: 1 }}
        />
        {statusStyle && (
          <Box sx={{ flex: "0 0 auto", width: "auto", display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 0.5 }}>
            <Typography sx={{ fontSize: "12px", color: "#64748B" }}>{translate.t("status")}</Typography>
            <Chip
              size="small"
              label={translate.t(statusStyle.label).toUpperCase()}
              sx={{ fontSize: "12px", fontWeight: 700, borderRadius: "8px", color: statusStyle.color, backgroundColor: statusStyle.background }}
            />
          </Box>
        )}
      </Box>

      <Typography sx={{ ...sectionTitleSx, mb: 1 }}>{translate.t("checklist_comments")}</Typography>
      <TextField
        fullWidth
        multiline
        minRows={5}
        placeholder={translate.t("checklist_comment_placeholder")}
        value={text}
        onChange={(event) => setText(event.target.value)}
        helperText={`${text.length}/${TEXT_MAX_LENGTH}`}
        slotProps={{ htmlInput: { maxLength: TEXT_MAX_LENGTH } }}
        sx={{ mb: 3 }}
      />

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1, mb: 2, borderBottom: "1px solid #E2E8F0" }}>
        <Typography sx={sectionTitleSx}>{translate.t("checklist_attached_files")}</Typography>
        <Button component="label" htmlFor={FILES_INPUT_ID} startIcon={<UploadFileOutlined />} sx={{ fontWeight: 600, color: "#003FFB" }}>
          {translate.t("checklist_attach").toUpperCase()}
        </Button>
      </Box>
      <input hidden id={FILES_INPUT_ID} type="file" multiple accept=".jpg,.jpeg,.png,.pdf,.xls,.xlsx" onChange={handleFilesChange} />

      {files.map((file, index) => (
        <Box
          key={`${file.name}-${index}`}
          sx={{ display: "flex", alignItems: "center", gap: 1, px: 2, py: 1, mb: 1, border: "1px solid #E2E8F0", borderRadius: "8px", backgroundColor: "#F9FAFC" }}>
          <InsertDriveFileOutlined sx={{ color: "#64748B" }} />
          <Tooltip title={file.name} arrow>
            <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: "14px", color: "#1B1F27" }}>
              {file.name}
            </Typography>
          </Tooltip>
          <IconButton size="small" onClick={() => setFiles(files.filter((selectedFile) => selectedFile !== file))}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
      ))}

      <Box
        component="label"
        htmlFor={FILES_INPUT_ID}
        sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1, p: 3, border: "1px dashed #E2E8F0", borderRadius: "4px", cursor: "pointer" }}>
        <UploadFileOutlined color="primary" />
        <Box component="span" sx={{ width: "auto", color: "#003FFB", textDecoration: "underline" }}>
          {translate.t("click_to_add_file")}
        </Box>
        <Typography sx={{ fontSize: "13px", color: "#606369" }}>{translate.t("checklist_attachment_types")}</Typography>
      </Box>
    </GenericDialog>
  );
};

export default NpiChecklistItemUpdateDialogComponent;